<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- breadcrumb -->
            <Breadcrumb
                :title="$tc('LBL_RETAILER_SALES')"
                :items="[{ label: $tc('LBL_INVOICE') }]"
                :home-label="$t('LBL_HOME')"
                :home-url="route('landing')"
            />
            <!-- breadcrumb end -->

            <!-- Performance Legend -->
            <div class="flex items-center justify-between bg-white dark:bg-gray-900 rounded-xl shadow p-4 mb-5">
                <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-200">Retailer Performance Overview</h2>
                <div class="flex items-center space-x-5">
                    <div class="flex items-center space-x-2">
                        <span class="w-3 h-3 rounded-full bg-green-500"></span>
                        <span class="text-sm text-gray-700 dark:text-gray-300">Top Retailer</span>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span class="w-3 h-3 rounded-full bg-orange-400"></span>
                        <span class="text-sm text-gray-700 dark:text-gray-300">Active</span>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span class="w-3 h-3 rounded-full bg-red-500"></span>
                        <span class="text-sm text-gray-700 dark:text-gray-300">Low Purchase</span>
                    </div>
                </div>
            </div>

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="retailer-sale-search-box"
                            v-model="search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-1 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for user"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <!-- <th class="px-4 py-3">#</th> -->
                                    <th class="px-4 py-3">RETAILER</th>
                                    <th class="px-4 py-3 text-right">QUANTITY SOLD</th>
                                    <th class="px-4 py-3 text-right">SALES AMOUNT</th>
                                    <th class="px-4 py-3 text-right">ORDERS</th>
                                    <th class="px-4 py-3 text-right">AVG ORDER VALUE</th>
                                    <th class="px-4 py-3 text-right">LAST PURCHASE</th>
                                    <th class="px-4 py-3">PERFORMANCE</th>
                                    <th class="">#INVOCIES</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in retailerBills.data">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                            row.credit ? 'bg-green-200' : '',
                                        ]"
                                    >
                                        <!-- <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td> -->
                                        <td class="px-4 py-3">
                                            {{ row[0].user.name }}
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            {{ sumQuantityFromBills(row) }}
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            {{ config(common.currency) }} {{ sumAmountFromBills(row) }}
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            {{ row.length }}
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            {{ config(common.currency) }} {{ averageOrderValue(row) }}
                                        </td>
                                        <td class="px-4 py-3 text-right text-gray-500">
                                            {{ lastPurchaseDate(row) | dateLabel }}
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="flex items-center space-x-2">
                                                <span
                                                    class="w-3 h-3 rounded-full"
                                                    :class="getPerformanceColor(row)"
                                                ></span>
                                                <span class="capitalize">{{ getPerformanceLabel(row) }}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <t-button
                                                variant="text"
                                                class="text-sm text-blue-600"
                                                @click="goto('reports.retailer.orders', { retailer: row[0].user })"
                                            >
                                                View Invoices
                                            </t-button>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!retailerBills.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="8"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="retailerBills.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!retailerBills.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import common from '../../common';
    import Breadcrumb from '../../Components/Breadcrumb.vue';

    export default {
        name: 'RetailerReport',

        components: {
            AppLayout,
            Breadcrumb,
        },

        props: {
            retailerBills: {
                type: Object,
                required: true,
            },
            oldSearch: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                common,
                search: this.oldSearch,
                currentPage: null,
                limit: 5,
                perPage: this.retailerBills.per_page,
                totalRows: this.retailerBills.total,
            };
        },

        computed: {
            disabled() {
                return !this.retailerBills.data.length;
            },
        },

        watch: {
            search: {
                handler() {
                    this.debounceFetchSearchResults();
                },
            },
        },

        mounted() {
            this.debounceFetchSearchResults = _.debounce(this.fetchSearchResults, 1000);
            document.getElementById('retailer-sale-search-box').focus();
        },

        created() {
            this.currentPage = this.retailerBills.current_page;
        },

        methods: {
            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.grand_total);
                });
                return sumGrandTotal.toFixed(2);
            },

            sumAmountFromBills(bills) {
                let sumGrandTotal = 0;
                bills.forEach((bill) => {
                    sumGrandTotal += parseFloat(this.sumAmountFromTransactions(bill.transactions));
                });
                return sumGrandTotal.toFixed(2);
            },

            sumQuantityFromTransactions(transactions) {
                let sumQty = 0;
                transactions.forEach((transaction) => {
                    sumQty += transaction.qty;
                });
                return sumQty;
            },

            sumQuantityFromBills(bills) {
                let sumQty = 0;
                bills.forEach((bill) => {
                    sumQty += this.sumQuantityFromTransactions(bill.transactions);
                });
                return sumQty;
            },

            averageOrderValue(bills) {
                const total = parseFloat(this.sumAmountFromBills(bills));
                const orders = bills.length || 1;
                return (total / orders).toFixed(2);
            },

            lastPurchaseDate(bills) {
                let lastDate = null;
                bills.forEach((bill) => {
                    bill.transactions.forEach((t) => {
                        const date = new Date(t.created_at);
                        if (!lastDate || date > lastDate) lastDate = date;
                    });
                });
                return lastDate ? new Date(lastDate).toLocaleDateString() : '-';
            },

            getPerformanceLabel(row) {
                const sales = parseFloat(this.sumAmountFromBills(row));
                if (sales > 50000) return 'Top Retailer';
                if (sales > 20000) return 'Active';
                return 'Low Purchase';
            },
            getPerformanceColor(row) {
                const sales = parseFloat(this.sumAmountFromBills(row));
                if (sales > 50000) return 'bg-green-500';
                if (sales > 20000) return 'bg-orange-400';
                return 'bg-red-500';
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('reports.retailer', {
                        page: newPage,
                        search: this.search,
                    }),
                );
            },

            fetchSearchResults() {
                this.$inertia.get(
                    route('reports.retailer', {
                        page: this.currentPage,
                        search: this.search,
                    }),
                );
            },
        },
    };
</script>
